import { useGetFrontRichiesteIdAllegati } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { EnumAllegatoRichiestaDTOTipoAllegato } from '@framework/api-client/api/inquiries-compile/schemas/enumAllegatoRichiestaDTOTipoAllegato.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type TipoDomandaDTO } from '@framework/api-client/api/inquiries-compile/schemas/tipoDomandaDTO.ts';
import { useGetFrontTipiDomandaId } from '@framework/api-client/api/inquiries-compile/tipi-domanda/tipi-domanda.ts';
import { useMemo } from 'react';
import { SubmissionLogic } from '../SubmissionLogic.ts';
import { SubmissionSummaryLogic } from './SubmissionSummaryLogic.ts';

export interface IUseSubmissionSummaryDataOptions {
  submission?: RichiestaDTO | null;
  inquiryType?: TipoDomandaDTO | null;
  isSubmissionComplete?: boolean;
}

/**
 * Hook che incapsula la logica di recupero dei dati per la sezione riepilogo firmato.
 */
export function useSubmissionSummaryData(
  options: IUseSubmissionSummaryDataOptions,
) {
  const {
    submission,
    inquiryType: inquiryTypeProp,
    isSubmissionComplete,
  } = options;

  const submissionId = submission?.id;

  const inquiryTypeQuery = useGetFrontTipiDomandaId(
    submission?.idTipoDomanda ?? '',
    {
      query: {
        keepPreviousData: true,
        enabled: Boolean(submission?.idTipoDomanda) && !inquiryTypeProp,
      },
    },
  );
  const inquiryType = inquiryTypeProp ?? inquiryTypeQuery.data;

  const extendedSummaryEnabled =
    SubmissionSummaryLogic.isExtendedSummaryEnabled(inquiryType);
  const uploadEnabled =
    SubmissionSummaryLogic.isSignedSummaryUploadEnabled(inquiryType);
  const acceptedFileExtensions =
    SubmissionSummaryLogic.getAcceptedFileExtensions(inquiryType);

  const isNew = SubmissionLogic.isSubmissionNew(submission);
  const canInteract = !isNew && (isSubmissionComplete ?? true);

  const attachmentsRequest = useGetFrontRichiesteIdAllegati(
    submissionId ?? '',
    {
      query: {
        keepPreviousData: true,
        enabled: Boolean(submissionId) && extendedSummaryEnabled,
      },
    },
  );

  const signedSummaryAttachment = useMemo(
    () =>
      attachmentsRequest.data?.find(
        a =>
          a.tipoAllegato ===
            EnumAllegatoRichiestaDTOTipoAllegato.RIEPILOGO_RICHIESTA &&
          a.idFile != null,
      ),
    [attachmentsRequest.data],
  );

  return {
    submissionId,
    inquiryType,
    extendedSummaryEnabled,
    uploadEnabled,
    acceptedFileExtensions,
    canInteract,
    signedSummaryAttachment,
    hasSignedSummary: signedSummaryAttachment != null,
    // Se il recupero degli allegati fallisce o è in corso lo stato firmato è
    // indeterminato: senza questi flag `hasSignedSummary = false`
    // farebbe scaricare in silenzio il riepilogo generato a runtime
    // al posto di quello firmato caricato dal richiedente.
    attachmentsError: attachmentsRequest.isError,
    attachmentsLoading: attachmentsRequest.isLoading,
  };
}
